import { startTransition, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NWClient } from '../../../client/NWClient';
import { DocumentHtmlDataModel } from '../../../models/document.html.data.model';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { sanitizeRedirectUrl } from '../../../common/helpers/sanitize';
import { addDocumentUploadHTML } from '../../../slices/document-details/documentDetailsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

export const useDocumentHTML = (
  htmlArray?: number[],
  allowGettingHTML?: boolean,
  documentGeneratedHTML?: string | null
) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const { documentId } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const contractGenerated = params.get('contractGenerated');
  const editDocumentParam = params.get('editDocument');
  const backURL = params.get('backURL');
  const [documentHTML, setDocumentHTML] = useState<string>('');
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [contentEditable, setContentEditable] = useState<boolean>(Boolean(editDocumentParam));
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [htmlIdRequested, setHTMLIdRequested] = useState<number | null>(null);

  const saveDocumentHTML = () => {
    setPendingStatus(true);
    if (contractGenerated) {
      const data = {
        user: currentUser.id,
        html: documentHTML,
      };
      NWClient.patch(`document-contract/${documentId}/`, null, data)
        .then(() => {
          toast.success(t('messages.documentUpdated'));
          setContentEditable(false);
          navigate(
            window.location.pathname +
              '?initialTab=0&contractGenerated=true' +
              (backURL ? '&backURL=' + encodeURIComponent(sanitizeRedirectUrl(backURL)) : '')
          );
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        })
        .finally(() => setPendingStatus(false));
    } else {
      const data = { html: documentHTML, document_upload: Number(documentId) };
      NWClient.post('document-upload-html', data)
        .then((res: any) => {
          localStorage.setItem('firstConversionFail', 'false');
          toast.success(t('messages.documentContentSaved'));
          setContentEditable(false);
          dispatch(addDocumentUploadHTML(res.id));
          navigate(window.location.pathname + '?initialTab=0');
        })
        .catch((error) => {
          if (localStorage.getItem('firstConversionFail') === 'true') {
            toast.error(getErrorMessage(error));
          } else {
            localStorage.setItem('firstConversionFail', 'true');
            toast.error(t('messages.firstDocumentConversionFail'));
          }
        })
        .finally(() => setPendingStatus(false));
    }
  };

  const fetchDocumentHTML = useCallback(
    () => {
      if (!contractGenerated && htmlArray && htmlArray.length > 0 && allowGettingHTML) {
        const htmlId = localStorage.getItem('doc_latest_version')
          ? Math.max(...htmlArray)
          : Math.min(...htmlArray);
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        startTransition(() => setHTMLIdRequested(htmlId));
        if (htmlId !== htmlIdRequested) {
          NWClient.get<DocumentHtmlDataModel>('document-upload-html-data', htmlId)
            .then((res) => {
              setDocumentHTML(res.html);
            })
            .catch((error) => {
              if (error.response && error.response.status !== 404) {
                toast.error(getErrorMessage(error));
              }
            })
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            .finally(() => setPendingStatus(false));
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      htmlArray,
      contractGenerated,
      allowGettingHTML,
      htmlIdRequested,
      /*documentHTML, getErrorMessage*/
    ]
  );

  useEffect(() => {
    if (documentGeneratedHTML) {
      startTransition(() => setDocumentHTML(documentGeneratedHTML));
    }
  }, [documentGeneratedHTML]);

  useEffect(() => {
    fetchDocumentHTML();
  }, [fetchDocumentHTML]);

  return {
    documentHTML,
    setDocumentHTML,
    pendingStatus,
    saveDocumentHTML,
    contentEditable,
    setContentEditable,
    showConfirmationModal,
    setShowConfirmationModal,
  };
};
